import React from "react";
import { CommonHelper } from "../../../../../../react-core/Helpers/CommonHelper";
import { CheckboxEditor } from "../../../../../../react-core/Html/CheckboxEditor";
import { MultiselectDropdown } from "../../../../../../react-core/Html/Components/MultiselectDropdown";
import { FormComponent } from "../../../../../../react-core/Html/FormComponent";
import { SelectEditor } from "../../../../../../react-core/Html/SelectEditor";
import { CompanySearchClient } from "../../../../../../Wrap/Api/Company/CompanySearch";
import { NetworkHistoryClient } from "../../../../../../Wrap/Api/Network/NetworkHistory";
import { CompanyDetailSort } from "../../../../../../Wrap/Constants/Enums/Domain/CompanyDetailSort";
import { FirmType } from "../../../../../../Wrap/Constants/Enums/Domain/FirmType";
import { HierarchyLevelName } from "../../../../../../Wrap/Constants/Enums/HierarchyLevelName";
import { NetworkType } from "../../../../../../Wrap/Constants/Enums/Network/NetworkType";
import { Adviser } from "../../../../../../Wrap/Models/Domains/Adviser/Adviser";
import { AlertDefinition } from "../../../../../../Wrap/Models/Domains/Threads/AlertDefinition";
import { AlertDefinitionCategory } from "../../../../../../Wrap/Models/Domains/Threads/AlertDefinitionCategory";
import { NotificationConfiguration } from "../../../../../../Wrap/Models/Domains/Threads/NotificationConfiguration";
import { NotificationConfigurationModel } from "../../../../../../Wrap/Models/Domains/Threads/NotificationConfigurationModel";
import { CompanySummary } from "../../../../../../Wrap/Models/Models/Company/CompanySummary";
import { NetworkHistoryModel } from "../../../../../../Wrap/Models/Models/Network/NetworkHistoryModel";
import { AdviserWizardAction } from "../../../../../Admin/Adviser/Actions/AdviserWizardAction";
import { HierarchyIdHelper } from "../../../../../Shared/Common/HierarchyIdHelper";
import { Alert } from "../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../Shared/Entities/Constants/AlertConstants";
import { SearchInput } from "../../../../../Shared/SearchInput/SearchInput";
import { titleLabels } from "../../../Clients/AddClient/Resource/Contents";
import { NotificationAction } from "../../Action/NotificationAction";

interface INewNotificationProps {
    model: NotificationConfigurationModel;
    hasFirmLevelPermission: boolean;
    onSave: () => void;
    onSetAdvisorSelection:(value: boolean) => void;
}

interface INewNotificationState {
    isLoading: boolean;

    selectedNetwork: NetworkHistoryModel;
    selectedCompany: CompanySummary;
    selectedAdviserIds: Array<number>;
    displayNetworkSearch: boolean;
    displayCompanySearch: boolean;
    availableAdvisors: Array<Adviser>;

    alertDefinitionCategories: Array<AlertDefinitionCategory>;
    selectedCategoryId: number;
    alertDefinitions: Array<AlertDefinition>;
    selectedNotificationId: number;
    selectedLevel: HierarchyLevelName;

    errorMsg: string[];
}

export class NewNotification extends FormComponent<INewNotificationProps, INewNotificationState> {
    constructor(props: INewNotificationProps) {
        super(props);
        this.state = {
            isLoading: false,

            selectedNetwork: null,
            selectedCompany: null,
            selectedAdviserIds: [],
            displayNetworkSearch: null,
            displayCompanySearch: null,
            availableAdvisors: [],

            alertDefinitionCategories: props?.model?.Categories,
            selectedCategoryId: null,
            alertDefinitions: props?.model?.AlertDefinitions,
            selectedNotificationId: null,
            selectedLevel: null,

            errorMsg: [],
        };
    }

    componentDidMount() {
        this.setState({
            isLoading: true,
        });

        this.getNetworksAndCompanies();
    }

    private getNetworksAndCompanies() {
        if (this.state.selectedCompany || this.state.selectedNetwork) {
            this.getAdviser(this.state.selectedNetwork?.Network, this.state.selectedCompany?.CompanyCode);
        }

        const networkClient = new NetworkHistoryClient();
        networkClient.get(null, NetworkType.Network, null, null, null, false, 10, 1).onSuccess((result) => {
            if (result.TotalRows > 1) {
                this.setState({ displayNetworkSearch: true });
            } else {
                this.setState({ displayNetworkSearch: false });
            }
        });

        const companyClient = new CompanySearchClient();
        companyClient
            .get(null, null, FirmType.All, null, null, null, true, 1, 10, CompanyDetailSort.CompanyName,false)
            .onSuccess((result) => {
                if (result.TotalNumberOfResults > 1) {
                    this.setState({ displayCompanySearch: true });
                } else if (result.TotalNumberOfResults == 1) {
                    this.getAdviser(null, result.PageOfResults[0].CompanyCode);
                    this.setState({ selectedCompany: result.PageOfResults[0], displayCompanySearch: false });
                } else {
                    this.setState({ displayCompanySearch: false });
                }
            });
    }

    private getAdviser(network, company) {
        if (CommonHelper.isNullOrEmpty(company) && CommonHelper.isNullOrEmpty(network)) {
            this.setState({ availableAdvisors: [] });
        } else {
            AdviserWizardAction.getAdviser(
                { Company: company, Network: network, CurrentPage: 1, ResultsPerPage: 999 },
                (result) => {
                    this.setState({
                        availableAdvisors: result.Results,
                    });
                }
            );
        }
    }

    isValid() {
        const {
            selectedNotificationId,
            selectedLevel,
            selectedAdviserIds,
            selectedCompany,
            alertDefinitions,
        } = this.state;
        const { model } = this.props;
        const errors = [];
        if (!selectedLevel) {
            errors.push("Please select a notification level");
        }
        if (!selectedNotificationId) {
            errors.push("Please select a notification");
        }
        if (selectedLevel == HierarchyLevelName.Company && !selectedCompany) {
            errors.push("Please select a company to apply the firm level setting");
        }
        if (selectedLevel == HierarchyLevelName.Advisor && CommonHelper.isNullOrEmpty(selectedAdviserIds)) {
            errors.push("Please select one or more advisers to apply the adviser level setting");
        }
        if (
            this.props.model?.ActiveNotifications?.some(
                (y) =>
                    y?.Alert == alertDefinitions?.find((x) => x.Id == selectedNotificationId)?.Name &&
                    ((selectedLevel == HierarchyLevelName.Company &&
                        y?.EntityType == 3 &&
                        HierarchyIdHelper.generateHierarchyId(selectedCompany?.HierarchyId)?.entityId == y?.EntityId) ||
                        (selectedLevel == HierarchyLevelName.Advisor &&
                            y?.EntityType == 2 &&
                            selectedAdviserIds?.some((x) => y?.EntityId == x)))
            )
        ) {
            errors.push("The notification for the selected company/adviser has already been turned on");
        }
        if (CommonHelper.isNullOrEmpty(errors)) {
            return true;
        } else {
            this.setState({ errorMsg: errors });
            return false;
        }
    }
    private onSave() {
        const isValid = this.isValid();
        if (isValid) {
            const { selectedNotificationId, selectedLevel, selectedAdviserIds, selectedCompany } = this.state;
            const newNotifications = [];
            const { onSetAdvisorSelection } = this.props;
            if (
                selectedLevel != null &&
                selectedNotificationId != null &&
                selectedLevel == HierarchyLevelName.Advisor
            ) {
                selectedAdviserIds.forEach((adviser) => {
                    newNotifications.push({
                        AlertDefinitionId: selectedNotificationId,
                        ReceiveAlert: true,
                        EntityType: selectedLevel,
                        EntityId: adviser,
                    });
                });

                onSetAdvisorSelection(this.state.availableAdvisors.length == this.state.selectedAdviserIds.length ? true : false);
            } else if (
                selectedLevel != null &&
                selectedNotificationId != null &&
                selectedLevel == HierarchyLevelName.Company
            ) {
                newNotifications.push({
                    AlertDefinitionId: selectedNotificationId,
                    ReceiveAlert: true,
                    EntityType: selectedLevel,
                    EntityId: HierarchyIdHelper.generateHierarchyId(selectedCompany?.HierarchyId)?.entityId,
                });
            }
            this.updateConfigurations(newNotifications);
        }
    }

    private updateConfigurations(configurations: Array<NotificationConfiguration>) {
        NotificationAction.updateNotificationConfiguration(configurations, () => this.props.onSave());
    }

    private renderErrorMsg() {
        const errors = [];
        this.state.errorMsg.forEach((msg) => {
            errors.push(<Alert alertType={AlertType.Error} message={msg} />);
        });
        return errors;
    }

    public render(): JSX.Element {
        const {
            alertDefinitionCategories,
            selectedCategoryId,
            alertDefinitions,
            selectedNotificationId,
            selectedLevel,
        } = this.state;
        const networkClient = new NetworkHistoryClient();
        const companyClient = new CompanySearchClient();

        return (
            <div>
                {this.renderErrorMsg()}

                <div className="row">
                    <div className="col-12 col-sm-fluid col-max-245">
                        <h2>New notification</h2>
                    </div>
                </div>
                <span>Add event notifications which are important to you</span>
                <div className="row">
                    {this.state.displayNetworkSearch && (
                        <div className="col-12 col-sm-fluid col-max-245">
                            <SearchInput
                                apiClient={(v) => {
                                    return networkClient.get(null, NetworkType.Unknown, v, null, null, false, 20, 1);
                                }}
                                onClick={(v) => {
                                    if (!v) {return;}
                                    this.getAdviser(v?.Network, null);
                                    this.setState({
                                        selectedNetwork: v,
                                        selectedCompany: null,
                                        selectedAdviserIds: [],
                                        availableAdvisors: [],
                                        errorMsg: [],
                                    });
                                }}
                                resultKey="networks"
                                getText={(v: NetworkHistoryModel) => v.NetworkName}
                                cssWrapper="control-size-430"
                                label={titleLabels.networkLabel}
                                value={this.state.selectedNetwork?.NetworkName}
                            />
                        </div>
                    )}
                    {this.state.displayCompanySearch && (
                        <div className="col-12 col-sm-fluid col-max-245">
                            <SearchInput
                                apiClient={(v) => {
                                    return companyClient.get(
                                        null,
                                        v,
                                        FirmType.All,
                                        null,
                                        this.state.selectedNetwork?.Network,
                                        null,
                                        true,
                                        1,
                                        20,
                                        CompanyDetailSort.CompanyName,
                                        false
                                    );
                                }}
                                resultKey={"PageOfResults"}
                                onClick={(v) => {
                                    if (!v) {return;}
                                    this.getAdviser(this.state.selectedNetwork?.Network, v?.CompanyCode);
                                    this.setState({
                                        selectedCompany: v,
                                        selectedAdviserIds: [],
                                        availableAdvisors: [],
                                        errorMsg: [],
                                    });
                                }}
                                getText={(v: CompanySummary) => v.CompanyName}
                                cssWrapper="control-size-430"
                                label={titleLabels.firmLabel}
                                value={this.state.selectedCompany?.CompanyName}
                            />
                        </div>
                    )}
                </div>
                {(this.state.selectedCompany || this.state.selectedNetwork) && (
                    <div>
                        <div className="row">
                            <div className="col-12 col-sm-fluid col-max-245">
                                <SelectEditor
                                    label="Category"
                                    options={[null].concat(alertDefinitionCategories.map((x) => x.Id))}
                                    selected={selectedCategoryId}
                                    getText={(v) => {
                                        if (v == null) {
                                            return "";
                                        }
                                        return alertDefinitionCategories.find((x) => x.Id == v).Name;
                                    }}
                                    onChange={(v) => {
                                        this.setState({
                                            selectedCategoryId: v,
                                            selectedNotificationId: null,
                                            selectedLevel: null,
                                            selectedAdviserIds: [],
                                            errorMsg: [],
                                        });
                                    }}
                                />
                            </div>
                            <div className="col-12 col-sm-fluid col-max-245">
                                <SelectEditor
                                    label="Notification"
                                    options={[null].concat(
                                        alertDefinitions
                                            .filter((x) => {
                                                if (selectedCategoryId == alertDefinitionCategories.length) {
                                                    return x.DefaultOn == true;
                                                }
                                                else {
                                                    return (
                                                        x.CategoryId == selectedCategoryId &&
                                                        x.Mandatory == false &&
                                                        !this.props.model?.ActiveNotifications.some(
                                                            (y) => y?.AlertDefinitionId == x?.Id && y?.Detail == "Default"
                                                        )
                                                    );
                                                }
                                            })
                                            .map((x) => x.Id)
                                    )}
                                    getText={(v) => {
                                        if (v == null) {
                                            return "";
                                        }
                                        return alertDefinitions.find((x) => x.Id == v).Name;
                                    }}
                                    selected={selectedNotificationId}
                                    onChange={(v) => {
                                        this.setState({
                                            selectedNotificationId: v,
                                            selectedLevel: null,
                                            selectedAdviserIds: [],
                                            errorMsg: [],
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12 col-sm-fluid col-max-245">
                                <SelectEditor
                                    label="Notification level"
                                    options={
                                        this.props.hasFirmLevelPermission
                                            ? [null, HierarchyLevelName.Advisor, HierarchyLevelName.Company]
                                            : [null, HierarchyLevelName.Advisor]
                                    }
                                    selected={selectedLevel}
                                    getText={(v) => {
                                        if (v == null) {
                                            return "";
                                        } else if (v == HierarchyLevelName.Advisor) {
                                            return "Adviser";
                                        }
                                        return "Firm";
                                    }}
                                    onChange={(v) => {
                                        this.setState({ selectedLevel: v, selectedAdviserIds: [], errorMsg: [] });
                                    }}
                                />
                            </div>
                            {selectedLevel == HierarchyLevelName.Advisor && (
                                <div className="col-12 col-sm-fluid col-max-245">
                                    <div className={"control-label"}>
                                        <label>Adviser</label>
                                    </div>
                                    <MultiselectDropdown
                                        scroll={{ maxHeight: 360 }}
                                        placeholder={
                                            this.state.selectedAdviserIds.length > 0
                                                ? this.state.selectedAdviserIds.length == 1
                                                    ? this.state.availableAdvisors.find(
                                                          (x) => x?.AdvisorId == this.state.selectedAdviserIds[0]
                                                      )?.Name
                                                    : this.state.availableAdvisors.length == this.state.selectedAdviserIds.length ? "All advisers" :"Multiple selected"
                                                : ""
                                        }
                                    >
                                        <div className="item is-all">
                                            <CheckboxEditor
                                                label={"All"}
                                                disabled={this.state.availableAdvisors.length == 0}
                                                onChange={(v) => {
                                                    let selectedAdviserIds = [];
                                                    if (v) {
                                                        selectedAdviserIds = this.state.availableAdvisors.map(
                                                            (i) => i.AdvisorId
                                                        );
                                                    } else {
                                                        selectedAdviserIds = [];
                                                    }
                                                    this.setState({ selectedAdviserIds: selectedAdviserIds });
                                                }}
                                                value={
                                                    this.state.availableAdvisors.length == 0 ||
                                                    this.state.availableAdvisors.length ==
                                                        this.state.selectedAdviserIds?.length
                                                }
                                            />
                                        </div>
                                        {(this.state.availableAdvisors || [])?.map((ele) => {
                                            return (
                                                <div className="item">
                                                    <CheckboxEditor
                                                        key={ele.AdvisorId}
                                                        label={ele.Name}
                                                        onChange={(v) => {
                                                            let { selectedAdviserIds } = this.state;
                                                            if (v) {
                                                                selectedAdviserIds.push(ele.AdvisorId);
                                                            } else {
                                                                selectedAdviserIds = selectedAdviserIds.filter(
                                                                    (item) => item != ele.AdvisorId
                                                                );
                                                            }
                                                            this.setState({
                                                                selectedAdviserIds: selectedAdviserIds,
                                                                errorMsg: [],
                                                            });
                                                        }}
                                                        value={(this.state.selectedAdviserIds || []).some(
                                                            (item) => item == ele.AdvisorId
                                                        )}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </MultiselectDropdown>
                                </div>
                            )}
                        </div>
                        <div className="row">
                            <div className="col-12 col-sm-fluid col-max-245">
                                <button
                                    className="next-button"
                                    onClick={() => {
                                        this.onSave();
                                    }}
                                >
                                    <span aria-hidden="true" className="icon"></span>
                                    <span className="text">Save</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
